'use client'

import { Suspense, useEffect, useRef } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { SuperTokensWrapper } from 'supertokens-auth-react'

import { useAuth } from '@/hooks/use-auth'
import { appQueryClient } from '@/lib/query-client'
import { subscribeToPersistedQueryCache } from '@/lib/react-query-persistence'
import { fetchAuthMe } from '@/queries/auth'
import { recordAuthDebug, summarizeAuthProfile } from '@/lib/auth-debug'
import { ensureSuperTokensClientInit } from '@/lib/supertokens-client'
import { TracewayInit } from '@/components/traceway-init'
import { captureTracewayException, recordTracewayBreadcrumb } from '@/lib/traceway-breadcrumbs'
import { ensureAuthStoreHydrated, useAuthStore } from '@/stores/auth-store'

// Initialize SuperTokens before the React tree mounts so its internal setup
// does not run as part of a component render.
ensureSuperTokensClientInit()

export function Providers({ children }: { children: React.ReactNode }) {
  const { bootstrap } = useAuth()
  const hasHydratedFromStorage = useAuthStore((state) => state.hasHydratedFromStorage)
  const status = useAuthStore((state) => state.status)
  const user = useAuthStore((state) => state.user)
  const refreshAttemptKey = useRef<string | null>(null)

  useEffect(() => {
    void ensureAuthStoreHydrated()
  }, [])

  useEffect(() => {
    void bootstrap()
  }, [bootstrap, hasHydratedFromStorage])

  useEffect(() => {
    if (!hasHydratedFromStorage || status !== 'authenticated' || !user) {
      return
    }

    const profileSummary = summarizeAuthProfile(user)

    if (!profileSummary.birthNameLooksPlaceholder && profileSummary.hasAvatarUrl) {
      refreshAttemptKey.current = null
      return
    }

    recordAuthDebug('auth:incomplete-profile-detected', profileSummary)
    recordTracewayBreadcrumb('auth', 'incomplete profile detected', {
      hasAvatarUrl: profileSummary.hasAvatarUrl,
      birthNameLooksPlaceholder: profileSummary.birthNameLooksPlaceholder,
      userId: profileSummary.userId,
    })

    const currentKey = `${user.user_id}:${user.birth_name ?? ''}:${user.avatar_url ?? ''}`
    if (refreshAttemptKey.current === currentKey) {
      recordAuthDebug('auth:incomplete-profile-refresh-skipped-duplicate', profileSummary)
      return
    }
    refreshAttemptKey.current = currentKey

    let cancelled = false

    async function refreshIncompleteProfile() {
      try {
        const data = await fetchAuthMe()
        if (cancelled) {
          return
        }
        useAuthStore.getState().setAuthenticated({
          user: data.user,
          identity: data.identity,
          session: data.session,
        })
        recordAuthDebug('auth:incomplete-profile-refresh-succeeded', {
          provider: data.identity.provider,
          ...summarizeAuthProfile(data.user),
        })
        recordTracewayBreadcrumb('auth', 'profile refresh succeeded', {
          provider: data.identity.provider,
          userId: data.user.user_id,
        })
      } catch (error) {
        recordAuthDebug('auth:incomplete-profile-refresh-failed', {
          errorMessage: error instanceof Error ? error.message : 'unknown_error',
          ...profileSummary,
        })
        recordTracewayBreadcrumb('auth', 'profile refresh failed', {
          userId: profileSummary.userId,
          errorMessage: error instanceof Error ? error.message : 'unknown_error',
        })
        void captureTracewayException(error, {
          boundary: 'auth-profile-refresh',
          userId: profileSummary.userId,
        })
      }
    }

    void refreshIncompleteProfile()

    return () => {
      cancelled = true
    }
  }, [hasHydratedFromStorage, status, user?.avatar_url, user?.birth_name, user?.user_id])

  useEffect(() => {
    return subscribeToPersistedQueryCache(appQueryClient, user?.user_id ?? null)
  }, [user?.user_id])

  return (
    <Suspense fallback={null}>
      <TracewayInit>
        <SuperTokensWrapper>
          <QueryClientProvider client={appQueryClient}>
            {children}
            <Toaster richColors position="top-right" />
          </QueryClientProvider>
        </SuperTokensWrapper>
      </TracewayInit>
    </Suspense>
  )
}
